import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import addressListSelector from 'selectors/order/addressListSelector';
import basketConstants from 'constants/Basket';
import store from 'store/Store';
import basket from 'reducers/addToBasket';
import basketUtils from 'utils/Basket';
import checkoutUtils from 'utils/Checkout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import paymentOptionsSelector from 'selectors/order/paymentOptionsSelector';
import Actions from 'Actions';
import FrictionlessCheckout from 'components/FrictionlessCheckout/FrictionlessCheckout';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import orderUtils from 'utils/Order';
import OrderActions from 'actions/OrderActions';
import { orderSelector } from 'selectors/order/orderSelector';
import { refreshCheckout } from 'services/Checkout';
import Empty from 'constants/empty';
import historyLocationSelector from 'selectors/historyLocation/historyLocationSelector';
import FrictionlessCheckoutEN from 'components/FrictionlessCheckout/locales/FrictionlessCheckout_en_US';
import FrictionlessCheckoutFR from 'components/FrictionlessCheckout/locales/FrictionlessCheckout_fr_CA';

const { showInfoModal } = Actions;
const { GIFT_MESSAGE_STATUS } = basketConstants;
const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(FrictionlessCheckoutEN, FrictionlessCheckoutFR);

const localization = createStructuredSelector({
    bopisTitle: getTextFromResource(getText, 'bopisTitle'),
    sadTitle: getTextFromResource(getText, 'sadTitle'),
    confirmSelection: getTextFromResource(getText, 'confirmSelection'),
    paypalAddressUnverified: getTextFromResource(getText, 'paypalAddressUnverified')
});

const { ACTION_TYPES } = basket;
const { SHIPPING_GROUPS } = orderUtils;

const creditCardsSelector = createSelector(paymentOptionsSelector, paymentOptions => paymentOptions?.creditCards || Empty.Array);
const isCAPAvailableSelector = createSelector(orderSelector, order => order.isCAPAvailable);

const basketUpdated = () => {
    return store.watchAction(ACTION_TYPES.UPDATE_BASKET, () => {
        refreshCheckout()();
    });
};

const disablePlaceOrder = () => checkoutUtils.disablePlaceOrderButtonBasedOnCheckoutCompleteness();

const validateZeroOrderWithCVVAndRedeemPoints = (higherRedeemPoints, isZeroDollarOrderWithCVVValidation) => {
    const zeroDollarHigherRedeemPointsCVV = higherRedeemPoints && isZeroDollarOrderWithCVVValidation;
    const zeroDollarHigherRedeemPointsNotCVV = higherRedeemPoints && !isZeroDollarOrderWithCVVValidation;

    return { zeroDollarHigherRedeemPointsCVV, zeroDollarHigherRedeemPointsNotCVV };
};

const fields = createSelector(
    orderDetailsSelector,
    addressListSelector,
    creditCardsSelector,
    paymentOptionsSelector,
    localization,
    isCAPAvailableSelector,
    historyLocationSelector,
    // eslint-disable-next-line complexity
    (orderDetails, addressList, creditCards, paymentOptions, locale, isCAPAvailable, historyLocation) => {
        const isBopisOrder = orderDetails?.header?.isBopisOrder;
        const hardGoodShippingGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
        const isZeroCheckout = orderUtils.isZeroCheckout();
        const isHalAvailable = orderDetails?.header?.isHalAvailable;
        const isSdd = orderUtils.hasSameDayDeliveryItems(orderDetails);
        const isAutoReplenishBasket = orderUtils.hasAutoReplenishItems(orderDetails);
        const isGis = orderUtils.hasStandardDeliveryItems(orderDetails) && orderUtils.hasStandardNonAutoReplenishItems(orderDetails);
        const isGiftCardAvailable =
            orderDetails?.header?.digitalGiftMessagingStatus !== GIFT_MESSAGE_STATUS.NOT_AVAILABLE && !isBopisOrder && !isAutoReplenishBasket;
        const hasRRC = orderUtils.hasRRC(orderDetails);
        const hasCCR = orderUtils.hasCCR(orderDetails);
        const hasAutoReplenishment = orderUtils.hasAutoReplenishItems(orderDetails);
        const isCBRPromoAppliedInBasket = basketUtils.isCBRPromoAppliedInBasket();
        const isSDUOnlyOrder = orderUtils.isSDUOnlyOrder(orderDetails);
        const hasSDUOnlyInSddBasket = orderUtils.hasSDUOnlyInSddBasket(orderDetails);
        const hasStandardItems = orderUtils.hasStandardDeliveryItems(orderDetails);
        let sameDayShippingGroup = orderUtils.resolveDeliverToShippingGroupEntry({
            shippingGroupEntries: orderDetails.shippingGroups?.shippingGroupsEntries,
            sduOnly: isSDUOnlyOrder,
            sduOnlyInSdd: hasSDUOnlyInSddBasket,
            standardItems: hasStandardItems
        });

        const isElectronicShippingGroup = sameDayShippingGroup && orderUtils.isElectronicShippingGroup(sameDayShippingGroup);
        const hasSDUInBasket = orderUtils.hasSDUInBasket(orderDetails);
        const physicalGiftCardShippingGroup = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
        const hardGoodOrGiftCardShippingGroup = hardGoodShippingGroup || physicalGiftCardShippingGroup;
        const isChangePermittedForShippingMethods = !isSdd || (isSdd && hasSDUOnlyInSddBasket);
        const hardGoodAddressComplete = hasStandardItems && hardGoodShippingGroup?.isComplete;
        const sddAddressComplete = isSdd && !hasSDUOnlyInSddBasket && sameDayShippingGroup?.shippingGroup?.isComplete;
        const giftCardAddressComplete = physicalGiftCardShippingGroup?.isComplete;
        const isShippableOrder = orderUtils.isShippableOrder(orderDetails);
        const needsShipping = isShippableOrder || orderUtils.isSddOrder(orderDetails);

        // Check for whether sections are complete, if those sections dont exist set them to complete
        // This way we can verify which of the present sections are incomplete
        // GC address completeness is only relevant when the GC delivery section is rendered.
        // For SDD orders the GC section is hidden, so treating it as complete avoids a
        // deadlock where isNewUserFlow stays true with no UI to resolve it.
        const shouldCheckGiftCardComplete = physicalGiftCardShippingGroup && !isSdd;

        const applicableCompletes = [
            hardGoodShippingGroup ? hardGoodAddressComplete : true,
            isSdd && !hasSDUOnlyInSddBasket && sameDayShippingGroup ? sddAddressComplete : true,
            shouldCheckGiftCardComplete ? giftCardAddressComplete : true
        ];
        const isNewUserFlow = needsShipping && !applicableCompletes.every(Boolean);
        const isZeroDollarOrderWithCVVValidation = orderUtils.isZeroDollarOrderWithCVVValidation();
        const orderHasPhysicalGiftCard = !!physicalGiftCardShippingGroup;
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const higherRedeemPoints = FrictionlessUtils.isHigherRedeemPointsOrder(orderDetails);
        const isFCFinalEnabled = FrictionlessUtils.isFCFinalReleaseEnabled();
        const { zeroDollarHigherRedeemPointsCVV, zeroDollarHigherRedeemPointsNotCVV } = validateZeroOrderWithCVVAndRedeemPoints(
            higherRedeemPoints,
            isZeroDollarOrderWithCVVValidation
        );
        const isEfullfillmentOrder = !isShippableOrder && isZeroCheckout;
        const hasPromoCodes = orderUtils.hasPromoCodes(orderDetails);
        const paymentGroups = orderDetails?.paymentGroups?.paymentGroupsEntries;
        const hasSavedOrDefaultAlternatePayment = FrictionlessUtils.hasSavedOrDefaultAlternatePayment(paymentOptions, orderDetails);

        const isRewardOnlyZeroDollarOrder =
            isZeroCheckout &&
            zeroDollarHigherRedeemPointsNotCVV &&
            !hasRRC &&
            !hasCCR &&
            !isCBRPromoAppliedInBasket &&
            !isZeroDollarOrderWithCVVValidation &&
            creditCards.length > 0;

        // Keep the payment section mounted on a $0 order when the user has saved cards
        // to manage and the zero-total cause is one of RRC or a CBR promo. Add new
        // zero-checkout variants to this list rather than appending another clause below.
        const shouldKeepPaymentSectionMountedForZeroCheckout = isZeroCheckout && creditCards.length > 0 && (hasRRC || isCBRPromoAppliedInBasket);

        const shouldRenderPayment =
            !isRewardOnlyZeroDollarOrder &&
            (!isZeroCheckout ||
                shouldKeepPaymentSectionMountedForZeroCheckout ||
                (isZeroCheckout && hasCCR) ||
                hasAutoReplenishment ||
                (isCBRPromoAppliedInBasket && !isFCFinalEnabled) ||
                isZeroDollarOrderWithCVVValidation ||
                (zeroDollarHigherRedeemPointsNotCVV && !creditCards.length && !isFCFinalEnabled) ||
                (zeroDollarHigherRedeemPointsNotCVV && !creditCards.length && isFCFinalEnabled && hasSavedOrDefaultAlternatePayment) ||
                (isFCFinalEnabled &&
                    !hasPromoCodes &&
                    !(isZeroCheckout && (isCBRPromoAppliedInBasket || zeroDollarHigherRedeemPointsNotCVV || hasRRC) && !paymentGroups?.length)) ||
                (isSdd && (!isZeroCheckout || isSDUOnlyOrder || hasAutoReplenishment || hasSDUInBasket || isElectronicShippingGroup)));

        const displayHalAvailability = orderUtils.isHalAvailable(isHalAvailable, isCAPAvailable);

        if (isElectronicShippingGroup && isSdd) {
            sameDayShippingGroup = orderDetails.shippingGroups?.shippingGroupsEntries?.find(
                group => group.shippingGroupType === SHIPPING_GROUPS.SAME_DAY
            );
        }

        return {
            isBopisOrder,
            hardGoodShippingGroup,
            isZeroCheckout,
            isHalAvailable: displayHalAvailability,
            isSdd,
            isAutoReplenishBasket,
            isGis,
            isNewUserFlow,
            localization: locale,
            isGiftCardAvailable,
            hasRRC,
            isSDUOnlyOrder,
            isElectronicShippingGroup,
            hasSDUInBasket,
            sameDayShippingGroup,
            hardGoodOrGiftCardShippingGroup,
            physicalGiftCardShippingGroup,
            hasStandardItems,
            isChangePermittedForShippingMethods,
            ...(isGiftCardAvailable && {
                orderId: orderDetails?.header?.orderId,
                giftMessagingStatus: orderDetails?.header?.digitalGiftMessagingStatus
            }),
            basketUpdated,
            shouldRenderPayment,
            orderDetails,
            addressList,
            paypalAddressUnverified: locale.paypalAddressUnverified,
            disablePlaceOrder,
            isShippableOrder,
            hasSDUOnlyInSddBasket,
            orderHasPhysicalGiftCard,
            isGuestCheckout,
            zeroDollarHigherRedeemPointsCVV,
            zeroDollarHigherRedeemPointsNotCVV,
            isEfullfillmentOrder,
            creditCardsCount: creditCards.length,
            hasPromoCodes,
            historyLocation,
            hasAutoReplenishment,
            isRewardOnlyZeroDollarOrder,
            defaultPayment: paymentOptions?.defaultPayment
        };
    }
);

const functions = {
    showInfoModal,
    setAddressNeedsConfirmation: OrderActions.setAddressNeedsConfirmation,
    setGiftCardAddressNeedsConfirmation: OrderActions.setGiftCardAddressNeedsConfirmation,
    setPaymentMethodNeedsConfirmation: OrderActions.setPaymentMethodNeedsConfirmation,
    setCheckoutSectionErrors: OrderActions.setCheckoutSectionErrors
};

const withFrictionlessCheckoutProps = wrapHOC(connect(fields, functions));

export default withFrictionlessCheckoutProps(FrictionlessCheckout);
